/* Fernwood motion v1 (2026-09-30). Spec: motion-spec-v1.md in the build folder.
   Runs only when <head> set html.fw-m (motion allowed: no reduced motion, no Save-Data, no 2G).
   Libraries load from cdnjs / jsdelivr after DOMContentLoaded, so the preloaded LCP poster is never delayed.
   Horizon scrolls .page-wrapper (not the window) at 990px and up: every ScrollTrigger and Lenis
   instance is built per breakpoint inside one gsap.matchMedia() with the matching scroller.
   Hero v2 (2026-10-01): the scroll film is gone from the home page (fw-film.js stays in the theme, unused);
   the home hero is the lookbook deck, see deck() below. Nothing here animates prices, variant pickers or Add to cart. */
(() => {
  const html = document.documentElement;
  if (!html.classList.contains('fw-m') || window.fwMotion) return;
  window.fwMotion = { version: 1 };

  const GSAP = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/';
  const LENIS = 'https://cdn.jsdelivr.net/npm/lenis@1.3.11/dist/lenis.min.js';
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const wrapper = document.querySelector('.page-wrapper');
  const MEDIA = '.fw-media, .fw-edits__cell, .fw-fits__item, .fw-fit__look, .fw-look, .fw-cats__cell';

  /* ---------- FH intro (the CSS plays it; this adds skip and cleanup) ---------- */
  new Promise((resolve) => {
    const intro = document.querySelector('.fw-intro');
    if (!intro || !html.classList.contains('fw-intro-on')) {
      intro?.remove();
      resolve();
      return;
    }
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      html.classList.remove('fw-intro-on');
      intro.remove();
      resolve();
    };
    const skip = () => {
      intro.classList.add('is-skip');
      window.setTimeout(done, 300);
    };
    intro.addEventListener('animationend', (event) => event.animationName === 'fw-intro-lift' && done());
    intro.addEventListener('click', skip);
    ['keydown', 'wheel', 'touchstart'].forEach((type) =>
      window.addEventListener(type, skip, { once: true, passive: true })
    );
    window.setTimeout(done, 2600); // never trap the page, even if animationend is missed
  });

  // Subresource integrity: a changed CDN file is refused and the page falls back to the CSS reveals.
  const SRI = {
    'gsap.min.js': 'sha384-HOvlOYPIs/zjoIkWUGXkVmXsjr8GuZLV+Q+rcPwmJOVZVpvTSXQChiN4t9Euv9Vc',
    'ScrollTrigger.min.js': 'sha384-P8VzCVnT9NBUkMrpcIZrJbA7EBjJvh/fJS6PmP+4nLIM284DtsImIv8D0fFjIkeh',
    'SplitText.min.js': 'sha384-xb96EMJeax+NLXMC88ZBa1xAeAW+kn+horHh/zFlbMLG2UPWhMJJSlv7fi57hS+Q',
    'lenis.min.js': 'sha384-kdY9hFBPoAPpxcrLihsO3APivFOxtQwSeYdzFD5CIIfLurwhdMrEHhkQxZJDkxgk',
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.crossOrigin = 'anonymous';
      s.integrity = SRI[src.split('/').pop()] || '';
      s.onload = resolve;
      s.onerror = () => reject(new Error(`fw-motion: could not load ${src}`));
      document.head.append(s);
    });

  const giveUp = () => html.classList.remove('fw-m-pending');

  async function boot() {
    try {
      await loadScript(`${GSAP}gsap.min.js`);
      await Promise.all([
        loadScript(`${GSAP}ScrollTrigger.min.js`),
        loadScript(`${GSAP}SplitText.min.js`),
        fine.matches ? loadScript(LENIS).catch(() => null) : null, // smooth scroll is optional
      ]);
    } catch (error) {
      giveUp(); // the CSS reveal system in fw-base.css keeps working without GSAP
      return;
    }
    try {
      run();
    } catch (error) {
      html.classList.remove('fw-gsap');
      giveUp();
      window.setTimeout(() => {
        throw error; // surface it in the console without breaking the page
      });
    }
  }

  function run() {
    const { gsap, ScrollTrigger, SplitText } = window;
    gsap.registerPlugin(ScrollTrigger, SplitText);
    ScrollTrigger.config({ ignoreMobileResize: true });
    html.classList.add('fw-gsap');

    const mm = gsap.matchMedia();
    mm.add(
      {
        desk: '(min-width: 990px)',
        mob: '(max-width: 989px)',
        fine: '(hover: hover) and (pointer: fine)',
        reduce: '(prefers-reduced-motion: reduce)',
      },
      (ctx) => {
        const c = ctx.conditions;
        if (c.reduce) {
          // switched to reduced motion mid-visit: hand everything back to the static CSS
          html.classList.remove('fw-gsap');
          giveUp();
          return undefined;
        }
        html.classList.add('fw-gsap');
        const ac = new AbortController();
        const scroller = wrapper && getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : window;
        ScrollTrigger.defaults({ scroller });
        const env = { gsap, ScrollTrigger, SplitText, scroller, desk: c.desk, fine: c.fine, signal: ac.signal };

        const lenis = c.fine ? startLenis(env) : null;
        env.lenis = lenis;
        const parts = [
          headings(env),
          deck(env),
          reveals(env),
          cards(env),
          drop(env),
          ticker(env),
          fits(env),
          story(env),
          magnetic(env),
          cursor(env),
          navRoll(env),
        ];
        giveUp();
        const refresh = () => ScrollTrigger.refresh();
        document.fonts?.ready.then(refresh);
        window.addEventListener('load', refresh, { once: true, signal: ac.signal });

        return () => {
          ac.abort();
          parts.forEach((cleanup) => typeof cleanup === 'function' && cleanup());
          lenis?.destroy();
          window.fwLenis = null;
        };
      }
    );

    // Theme editor: re-measure when a section is re-rendered.
    document.addEventListener('shopify:section:load', () => ScrollTrigger.refresh());
  }

  /* ---------- Lenis smooth scroll (fine pointers only; phones keep native scrolling) ---------- */
  function startLenis({ gsap, ScrollTrigger, scroller, signal }) {
    if (!window.Lenis) return null;
    const onWrapper = scroller !== window;
    const lenis = new window.Lenis({
      wrapper: onWrapper ? scroller : window,
      content: onWrapper ? scroller : document.documentElement,
      lerp: 0.1,
      wheelMultiplier: 1,
      smoothWheel: true,
      autoRaf: false,
      allowNestedScroll: true,
      prevent: (node) =>
        Boolean(node.closest?.('dialog, [data-lenis-prevent], .menu-drawer, .facets, .fw-quick-view, [role="dialog"]')),
    });
    window.fwLenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // Horizon locks scrolling (html[scroll-lock]) while drawers and modals are open.
    const sync = () => (html.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start());
    const lockObserver = new MutationObserver(sync);
    lockObserver.observe(html, { attributes: true, attributeFilter: ['scroll-lock'] });
    signal.addEventListener('abort', () => {
      lockObserver.disconnect();
      gsap.ticker.remove(tick);
    });

    // In-page anchors glide instead of jumping.
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest?.('a[href^="#"]:not([href="#"])');
        if (!link || event.defaultPrevented) return;
        const target = document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1)));
        if (!target || target.closest('dialog')) return;
        event.preventDefault();
        lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      },
      { signal }
    );
    return lenis;
  }

  /* ---------- Section headlines: masked lines rise with a slight rotation ----------
     Page titles (.fw-h1, product title) are left to the CSS wipe in fw-motion.css: they can be the LCP
     element, so they must paint without waiting for this file. */
  function headings({ gsap, SplitText }) {
    const targets = [...document.querySelectorAll('.fw-h2')].filter(
      (el) => !el.closest('.fw-film, .fw-drop, .fw-mnav, dialog, [hidden]')
    );
    const splits = targets.map((el) =>
      SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'fw-line',
        autoSplit: true,
        onSplit(self) {
          return gsap.from(self.lines, {
            yPercent: 118,
            rotate: 2.5,
            transformOrigin: '0% 100%',
            duration: 1.15,
            ease: 'expo.out',
            stagger: 0.085,
            scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          });
        },
      })
    );
    return () => splits.forEach((s) => s.revert());
  }

  /* ---------- Hero v2, lookbook deck (sections/fw-deck.liquid), desktop only ----------
     The deal-in on load is pure CSS (`translate` / `rotate`), so it never fights GSAP's `transform`.
     Scroll: the hero pins for one screen; the hand opens into a straight row while each card turns over once
     (its back carries the old film chapter line), the cover steps back and the copy lifts clear of the row.
     Cursor: the deck tilts in 3D and outer cards move more than inner ones. Phones keep the CSS swipe row.
     Everything is measured with offsets (not rects), so a pinned or mid-scrub state never skews the maths. */
  function deck(env) {
    const { gsap, desk, fine, signal } = env;
    const root = document.querySelector('[data-fw-deck]');
    if (!root || !desk) return undefined;
    const stage = root.querySelector('.fw-deck__stage');
    const table = root.querySelector('.fw-deck__table');
    const hand = root.querySelector('[data-fw-deck-hand]');
    const cover = root.querySelector('.fw-deck__cover');
    const coverImg = cover?.querySelector('img');
    const copy = root.querySelector('.fw-deck__copy');
    const actions = root.querySelector('.fw-deck__actions');
    // the outer masked line drifts; its inner span keeps the CSS rise (never tween an element mid CSS `translate`)
    const lines = [...root.querySelectorAll('.fw-deck__line')];
    const cards = [...root.querySelectorAll('.fw-deck__card')];
    if (!stage || !table || !hand || !copy || !cards.length) return undefined;

    const n = cards.length;
    const FAN = 26; // degrees across the whole hand, same as --deck-fan in the section CSS
    const SPREAD = 0.86; // share of the table width the fan spans (86cqw in the section CSS)
    const SAG = 0.1; // how far the outer cards drop, as a share of the table height (10cqh)
    const tOf = (i) => (n > 1 ? i / (n - 1) - 0.5 : 0);
    const geo = {};
    const measure = () => {
      const tw = table.clientWidth;
      const th = table.clientHeight;
      const cw = cards[0].offsetWidth;
      const ch = cards[0].offsetHeight;
      const s = getComputedStyle(stage);
      const padL = parseFloat(s.paddingLeft) || 0;
      const innerW = stage.clientWidth - padL - (parseFloat(s.paddingRight) || 0);
      const gap = Math.max(12, Math.min(28, (innerW - n * cw) / Math.max(1, n - 1)));
      const rowW = n * cw + (n - 1) * gap;
      const rowTop = th * 0.93 - ch; // cards sit 7cqh above the table bottom
      const actionsBottom = copy.offsetTop + (actions ? actions.offsetTop + actions.offsetHeight : copy.offsetHeight);
      Object.assign(geo, {
        tw,
        th,
        cw,
        gap,
        rowLeft: stage.offsetLeft + padL + (innerW - rowW) / 2 - table.offsetLeft,
        baseLeft: tw / 2 - cw / 2,
        copyLift: Math.min(0, table.offsetTop + rowTop - 32 - actionsBottom),
        coverScale: cover ? Math.min(1, Math.max(0.5, (rowTop - 24 - cover.offsetTop) / cover.offsetHeight)) : 1,
      });
    };
    measure();

    const tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: '+=100%',
        scrub: 1,
        pin: root.closest('.shopify-section') || root,
        pinSpacing: true, // main is display:flex, where ScrollTrigger would default to false
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onRefreshInit: measure,
      },
    });
    cards.forEach((card, i) => {
      tl.fromTo(
        card,
        {
          x: () => tOf(i) * (geo.tw * SPREAD - geo.cw),
          y: () => tOf(i) ** 2 * geo.th * SAG,
          rotation: tOf(i) * FAN,
          rotationY: 0,
          transformOrigin: '50% 100%',
        },
        { x: () => geo.rowLeft + i * (geo.cw + geo.gap) - geo.baseLeft, y: 0, rotation: 0, rotationY: 360, duration: 0.8 },
        0.04 + 0.12 * (n > 1 ? i / (n - 1) : 0)
      );
    });
    if (cover) tl.fromTo(cover, { scale: 1 }, { scale: () => geo.coverScale, duration: 0.9 }, 0);
    if (coverImg) tl.fromTo(coverImg, { scale: 1 }, { scale: 1.12, duration: 1 }, 0);
    tl.fromTo(copy, { y: 0 }, { y: () => geo.copyLift, duration: 0.8 }, 0.05);
    if (lines[0]) tl.fromTo(lines[0], { xPercent: 0 }, { xPercent: -5, duration: 1 }, 0);
    if (lines[1]) tl.fromTo(lines[1], { xPercent: 0 }, { xPercent: 9, duration: 1 }, 0);
    tl.to({}, { duration: 0.12 }); // hold the finished row for a beat before the pin releases

    if (fine) {
      const ease = { duration: 0.9, ease: 'power3.out' };
      const rx = gsap.quickTo(hand, 'rotationX', ease);
      const ry = gsap.quickTo(hand, 'rotationY', ease);
      const layers = cards.map((card, i) => {
        const link = card.querySelector('.fw-deck__link');
        return { x: gsap.quickTo(link, 'x', ease), y: gsap.quickTo(link, 'y', ease), depth: 6 + Math.abs(tOf(i)) * 30 };
      });
      const move = (px, py) => {
        rx(-py * 5);
        ry(px * 7);
        layers.forEach((l) => {
          l.x(px * l.depth);
          l.y(py * l.depth * 0.6);
        });
      };
      root.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          const r = root.getBoundingClientRect();
          move((event.clientX - r.left) / r.width - 0.5, (event.clientY - r.top) / r.height - 0.5);
        },
        { signal, passive: true }
      );
      root.addEventListener('pointerleave', () => move(0, 0), { signal });
    }
    return undefined; // gsap.matchMedia() reverts every tween and ScrollTrigger made in here
  }

  /* Keyboard help for pinned scenes: jump the scroll to y on the next frame (after the browser's own
     scroll-into-view for the focused element), so the focused control is on screen and fully shown. */
  const jumpTo = ({ lenis, scroller }, y, reveal) =>
    window.requestAnimationFrame(() => {
      const box = scroller === window ? document.scrollingElement : scroller;
      if (lenis) lenis.scrollTo(y, { immediate: true, force: true });
      else box.scrollTop = y;
      if (!reveal) return;
      // past the pin the page scrolls normally again: bring the control itself to mid-screen
      window.requestAnimationFrame(() => {
        const r = reveal.getBoundingClientRect();
        if (r.top >= 0 && r.bottom <= window.innerHeight) return;
        const to = box.scrollTop + r.top - window.innerHeight * 0.5;
        if (lenis) lenis.scrollTo(to, { immediate: true, force: true });
        else box.scrollTop = to;
      });
    });

  /* ---------- Reveals: photos wipe open from the bottom, blocks rise ----------
     Hidden states use opacity only (never visibility), so links inside stay in the tab order. */
  const inHorizontalTrack = (el) => Boolean(el.closest('.fw-rail__track, .fw-spot__list, [data-fw-drag]'));

  function reveals({ gsap, desk }) {
    document.querySelectorAll('[data-fw-reveal]').forEach((el) => {
      const skip =
        inHorizontalTrack(el) ||
        el.closest('.fw-drop') ||
        (desk && el.closest('.fw-fits')) ||
        el.matches('.fw-h1, .fw-h2') ||
        el.querySelector('.fw-card');
      if (skip) {
        el.classList.add('is-in');
        return;
      }
      const delay = Math.min(parseFloat(getComputedStyle(el).getPropertyValue('--fw-i')) || 0, 6) * 0.08;
      const trigger = { trigger: el, start: 'top 88%', once: true };
      if (el.matches(MEDIA)) {
        const img = el.querySelector('img');
        const parallax = el.matches('.fw-parallax') || el.closest('.fw-parallax');
        gsap.fromTo(
          el,
          { clipPath: 'inset(100% 0% 0% 0%)' },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.35,
            ease: 'expo.out',
            delay,
            clearProps: 'clipPath',
            scrollTrigger: { ...trigger, onEnter: () => el.classList.add('is-in') },
          }
        );
        if (img && !parallax) {
          gsap.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 1.8, ease: 'expo.out', delay, clearProps: 'transform', scrollTrigger: trigger });
        }
      } else {
        gsap.from(el, {
          y: 44,
          opacity: 0,
          duration: 1.1,
          ease: 'expo.out',
          delay,
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { ...trigger, onEnter: () => el.classList.add('is-in') },
        });
      }
    });
  }

  /* ---------- Product cards in grids: batched rise with a small 3D tilt-back ---------- */
  function cards({ gsap, ScrollTrigger }) {
    const list = [...document.querySelectorAll('.fw-card')].filter((card) => !inHorizontalTrack(card));
    const below = list.filter((card) => card.getBoundingClientRect().top > window.innerHeight * 0.92);
    if (!below.length) return undefined;
    gsap.set(below, { opacity: 0, y: 70, rotateX: -14, transformPerspective: 1000, transformOrigin: '50% 0%' });
    const batches = ScrollTrigger.batch(below, {
      start: 'top 94%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 1.1,
          ease: 'expo.out',
          stagger: 0.07,
          clearProps: 'transform,opacity,visibility',
        }),
    });
    return () => batches.forEach((t) => t.kill());
  }

  /* ---------- Drop 02: pinned opening on desktop, scrubbed wipe on phones, WebGL on fine pointers ---------- */
  function drop(env) {
    const { gsap, SplitText, desk, fine } = env;
    const section = document.querySelector('.fw-drop');
    const media = section?.querySelector('.fw-drop__media');
    if (!section || !media) return undefined;
    const img = media.querySelector(':scope > img');
    const copy = section.querySelector('.fw-drop__copy');
    const title = copy?.querySelector('h2');
    const bits = copy ? [...copy.children].filter((el) => el !== title) : [];
    media.classList.add('is-in'); // the stamp "thunk" in fw-sections.css keys off this
    copy?.classList.add('is-in');
    const split = title ? SplitText.create(title, { type: 'words,chars', mask: 'words', charsClass: 'fw-char' }) : null;
    let gl = null;

    if (desk) {
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'center center',
          end: '+=110%',
          scrub: 1,
          pin: section.closest('.shopify-section') || section,
          pinSpacing: true, // main is display:flex, where ScrollTrigger would default to false
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
      tl.fromTo(media, { clipPath: 'inset(16% 24% 16% 24%)' }, { clipPath: 'inset(-12% -12% -12% -12%)', duration: 1 }, 0);
      if (img) tl.fromTo(img, { scale: 1.12 }, { scale: 1, duration: 1 }, 0); // v4: was 1.4; the inset frame does the opening, so the raster is barely upscaled
      if (split) tl.from(split.chars, { yPercent: 120, rotate: 8, stagger: 0.03, duration: 0.4, ease: 'power3.out' }, 0.04);
      if (bits.length) tl.from(bits, { y: 40, opacity: 0, stagger: 0.1, duration: 0.35, ease: 'power2.out' }, 0.4);
      // Tabbing to the CTA mid-scene jumps to the end of the pin, where the copy and button are fully shown.
      const onFocus = () => {
        const st = tl.scrollTrigger;
        if (st && st.progress < 1) jumpTo(env, st.end);
      };
      section.addEventListener('focusin', onFocus, { signal: env.signal });
    } else {
      gsap.fromTo(
        media,
        { clipPath: 'inset(10% 8% 10% 8%)' },
        { clipPath: 'inset(-12% -12% -12% -12%)', ease: 'none', scrollTrigger: { trigger: media, start: 'top 95%', end: 'top 35%', scrub: 0.6 } }
      );
      if (img) gsap.fromTo(img, { scale: 1.1 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: media, start: 'top 95%', end: 'bottom 40%', scrub: 0.6 } });
      if (split)
        gsap.from(split.chars, { yPercent: 120, duration: 0.9, ease: 'expo.out', stagger: 0.03, scrollTrigger: { trigger: title, start: 'top 90%', once: true } });
      if (bits.length)
        gsap.from(bits, { y: 30, opacity: 0, duration: 1, ease: 'expo.out', stagger: 0.1, clearProps: 'all', scrollTrigger: { trigger: copy, start: 'top 88%', once: true } });
    }

    if (fine && img) {
      media.dataset.fwCursor = media.dataset.fwCursor || 'Drop 02';
      gl = dropGL({ gsap, media, img });
    }
    return () => {
      split?.revert();
      gl?.destroy();
    };
  }

  /* Raw WebGL (no Three.js: one textured quad does not need a 600 KB scene graph). A ripple and an RGB
     split follow the cursor; the split grows with pointer speed. It only renders while the photo is on screen. */
  function dropGL({ gsap, media, img }) {
    const canvas = document.createElement('canvas');
    canvas.className = 'fw-drop__gl';
    canvas.setAttribute('aria-hidden', 'true');
    // v4: WebGL2 first, because it can mipmap a non-power-of-two photo (no shimmer when the 2400px texture
    // is drawn smaller); WebGL1 is the fallback. highp where the GPU has it: mediump (fp16 on Apple GPUs)
    // cannot address the texels of a 2400px texture exactly and stair-steps the photo.
    const opts = { alpha: false, antialias: false, premultipliedAlpha: false };
    const gl = canvas.getContext('webgl2', opts) || canvas.getContext('webgl', opts);
    if (!gl) return null;
    const isGL2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    const high = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    const precision = high && high.precision > 0 ? 'highp' : 'mediump';
    const VS = 'attribute vec2 p;varying vec2 v;void main(){v=p*.5+.5;gl_Position=vec4(p,0.,1.);}';
    const FS = `precision ${precision} float;
      uniform sampler2D t;uniform vec2 res,isz,m;uniform float h,vel,time,sc;varying vec2 v;
      vec2 cover(vec2 uv){float rs=res.x/res.y,ri=isz.x/isz.y;vec2 s=rs<ri?vec2(rs/ri,1.):vec2(1.,ri/rs);return (uv-.5)*s/sc+.5;}
      void main(){vec2 d=v-m;d.x*=res.x/res.y;float dist=length(d);
        vec2 dir=dist>.0001?d/dist:vec2(0.);
        float ripple=sin(dist*34.-time*5.)*exp(-dist*7.)*.010*h;
        float bulge=smoothstep(.38,0.,dist)*.05*h;
        vec2 uv=cover(v+dir*(ripple-bulge*dist));
        float sp=(.003+vel*.03)*h*smoothstep(.45,0.,dist);
        gl_FragColor=vec4(texture2D(t,uv+vec2(sp,0.)).r,texture2D(t,uv).g,texture2D(t,uv-vec2(sp,0.)).b,1.);}`;
    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    };
    const vs = compile(gl.VERTEX_SHADER, VS);
    const fs = compile(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return null;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const u = Object.fromEntries(['t', 'res', 'isz', 'm', 'h', 'vel', 'time', 'sc'].map((n) => [n, gl.getUniformLocation(prog, n)]));

    const state = { mx: 0.5, my: 0.5, tx: 0.5, ty: 0.5, h: 0, th: 0, vel: 0, ready: false, visible: false, lastX: 0, lastY: 0 };
    const tex = gl.createTexture();
    const source = new Image();
    source.decoding = 'async';
    source.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
      if (isGL2) gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, isGL2 ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform2f(u.isz, source.naturalWidth, source.naturalHeight);
      state.ready = true;
      canvas.classList.add('is-ready');
    };
    source.onerror = () => canvas.remove();
    // Same-origin Shopify CDN path, so the canvas is never tainted. Wait for the lazy <img> so the texture is
    // the srcset candidate the browser picked for this screen (img.src alone is a fixed width).
    const useCurrent = () => {
      source.src = img.currentSrc || img.src;
    };
    if (img.complete && img.currentSrc) useCurrent();
    else img.addEventListener('load', useCurrent, { once: true });

    const size = () => {
      const r = media.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2); // v4: was 1.5, which drew the whole photo soft on 2x screens
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(u.res, canvas.width, canvas.height);
    };
    img.after(canvas);
    size();
    const ro = new ResizeObserver(size);
    ro.observe(media);
    const io = new IntersectionObserver(([entry]) => {
      state.visible = entry.isIntersecting;
    });
    io.observe(media);

    const move = (event) => {
      const r = media.getBoundingClientRect();
      state.tx = (event.clientX - r.left) / r.width;
      state.ty = 1 - (event.clientY - r.top) / r.height;
      const speed = Math.hypot(event.clientX - state.lastX, event.clientY - state.lastY);
      state.lastX = event.clientX;
      state.lastY = event.clientY;
      state.vel = Math.min(1, state.vel * 0.6 + speed / 90);
    };
    const enter = (event) => {
      state.th = 1;
      state.lastX = event.clientX;
      state.lastY = event.clientY;
    };
    const leave = () => {
      state.th = 0;
    };
    media.addEventListener('pointermove', move);
    media.addEventListener('pointerenter', enter);
    media.addEventListener('pointerleave', leave);

    const render = (time) => {
      if (!state.ready || !state.visible) return;
      state.mx += (state.tx - state.mx) * 0.12;
      state.my += (state.ty - state.my) * 0.12;
      state.h += (state.th - state.h) * 0.08;
      state.vel *= 0.94;
      gl.uniform2f(u.m, state.mx, state.my);
      gl.uniform1f(u.h, state.h);
      gl.uniform1f(u.vel, state.vel);
      gl.uniform1f(u.time, time);
      gl.uniform1f(u.sc, Number(gsap.getProperty(img, 'scale')) || 1); // follows the pinned push-in
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    gsap.ticker.add(render);
    return {
      destroy() {
        gsap.ticker.remove(render);
        ro.disconnect();
        io.disconnect();
        media.removeEventListener('pointermove', move);
        media.removeEventListener('pointerenter', enter);
        media.removeEventListener('pointerleave', leave);
        canvas.remove();
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      },
    };
  }

  /* ---------- Ticker: loop speed and skew follow scroll velocity; direction follows scroll direction ---------- */
  function ticker({ gsap, ScrollTrigger, desk }) {
    const root = document.querySelector('[data-fw-ticker]');
    const bands = root ? [...root.querySelectorAll('[data-fw-ticker-band]')] : [];
    if (!bands.length) return undefined;
    const loops = bands.map((band, i) => {
      const track = band.querySelector('[data-fw-ticker-track]');
      const reverse = i % 2 === 1;
      const tween = gsap.fromTo(
        track,
        { xPercent: reverse ? -50 : 0 },
        { xPercent: reverse ? 0 : -50, duration: desk ? 42 : 30, ease: 'none', repeat: -1, paused: true }
      );
      return { tween, skew: gsap.quickTo(track, 'skewX', { duration: 0.5, ease: 'power3.out' }) };
    });
    const settle = gsap.delayedCall(0.2, () => loops.forEach((l) => l.skew(0))).pause();
    ScrollTrigger.create({
      trigger: root,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: (self) => loops.forEach((l) => (self.isActive ? l.tween.play() : l.tween.pause())),
      onUpdate: (self) => {
        const v = self.getVelocity();
        const boost = 1 + Math.min(Math.abs(v) / 250, 5);
        loops.forEach((l) => {
          gsap.to(l.tween, { timeScale: self.direction * boost, duration: 0.25, overwrite: true });
          gsap.to(l.tween, { timeScale: self.direction, duration: 1.2, delay: 0.25, ease: 'power2.out' });
          l.skew(gsap.utils.clamp(-10, 10, v / -260));
        });
        settle.restart(true);
      },
    });
    return () => settle.kill();
  }

  /* ---------- Student fits: pinned horizontal lookbook on desktop ---------- */
  function fits(env) {
    const { gsap, desk } = env;
    const section = document.querySelector('.fw-fits');
    const wall = section?.querySelector('.fw-fits__wall');
    if (!desk || !section || !wall || wall.children.length < 4) return undefined;
    section.classList.add('is-hscroll');
    const items = [...wall.children];
    items.forEach((item) => item.classList.add('is-in'));
    const header = () => document.querySelector('#header-component')?.offsetHeight || 0;
    const distance = () => Math.max(0, wall.scrollWidth - wall.parentElement.clientWidth);
    const host = section.closest('.shopify-section') || section;
    const tween = gsap.to(wall, {
      x: () => -distance(),
      ease: 'none',
      scrollTrigger: {
        trigger: host,
        start: () => `top top+=${header()}`,
        end: () => `+=${distance() * 1.6}`, // slower than 1:1 so the row glides
        pin: host,
        scrub: 1,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });
    gsap.from(items, {
      y: 90,
      opacity: 0,
      duration: 1.2,
      ease: 'expo.out',
      stagger: 0.07,
      clearProps: 'opacity,visibility',
      scrollTrigger: { trigger: host, start: 'top 75%', once: true },
    });
    // Each photo drifts inside its frame while the row moves (lookbook parallax).
    items.forEach((item) => {
      const img = item.querySelector('.fw-fits__media img');
      if (!img) return;
      gsap.fromTo(
        img,
        { xPercent: -5 },
        { xPercent: 5, ease: 'none', scrollTrigger: { trigger: item, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true } }
      );
    });
    // Keyboard: tabbing to a hotspot or link in an off-screen photo scrolls the row to it.
    const onFocus = (event) => {
      const item = event.target.closest('.fw-fits__item');
      const st = tween.scrollTrigger;
      if (!st) return;
      if (!item) {
        if (event.target.closest('.fw-fits__wall') || st.progress >= 1) return;
        jumpTo(env, st.end + 1, event.target); // e.g. "See all fits" sits below the row
        return;
      }
      const p = gsap.utils.clamp(0, 1, (item.offsetLeft - wall.parentElement.clientWidth * 0.3) / (distance() || 1));
      jumpTo(env, st.start + p * (st.end - st.start));
    };
    section.addEventListener('focusin', onFocus);
    return () => {
      section.removeEventListener('focusin', onFocus);
      section.classList.remove('is-hscroll');
    };
  }

  /* ---------- Brand story: the rounded ink card floods to full bleed ---------- */
  function story({ gsap }) {
    const el = document.querySelector('.fw-story');
    if (!el) return undefined;
    gsap.fromTo(
      el,
      { clipPath: 'inset(0% 2.5% 0% 2.5% round 28px)' },
      { clipPath: 'inset(0% 0% 0% 0% round 0px)', ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 20%', scrub: 1 } }
    );
    return undefined;
  }

  /* ---------- Magnetic buttons (fine pointers; never on Add to cart or form submits) ---------- */
  function magnetic({ gsap, fine, signal }) {
    if (!fine) return undefined;
    const els = [...document.querySelectorAll('.fw-btn, .fw-rail__arrows button, .header-actions__action, .fw-chip')].filter(
      (el) => !el.closest('form, dialog, .fw-film') && !el.dataset.fwMagnet && el.type !== 'submit'
    );
    els.forEach((el) => {
      el.dataset.fwMagnet = 'gsap';
      const strength = el.matches('.fw-btn') ? 0.3 : 0.22;
      const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'power3.out' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3.out' });
      el.addEventListener(
        'pointermove',
        (event) => {
          const r = el.getBoundingClientRect();
          xTo((event.clientX - r.left - r.width / 2) * strength);
          yTo((event.clientY - r.top - r.height / 2) * strength);
        },
        { signal }
      );
      el.addEventListener(
        'pointerleave',
        () => {
          xTo(0);
          yTo(0);
        },
        { signal }
      );
    });
    return () =>
      els.forEach((el) => {
        delete el.dataset.fwMagnet;
        gsap.set(el, { clearProps: 'transform' });
      });
  }

  /* ---------- Cursor: a lagging cobalt ring that becomes a label over photos ---------- */
  const LABELS = [
    ['[data-fw-cursor]', (el) => el.dataset.fwCursor],
    ['.fw-rail__track, .fw-spot__list', () => 'Drag'],
    ['.fw-edits__link', () => 'Shop'],
    ['.fw-fits__media', () => 'Shop the fit'],
    ['.fw-card__media, .fw-card__image-link', () => 'View'],
  ];

  function cursor({ gsap, fine, signal }) {
    if (!fine) return undefined;
    const el = document.createElement('div');
    el.className = 'fw-cursor';
    el.setAttribute('aria-hidden', 'true');
    const ring = document.createElement('span');
    ring.className = 'fw-cursor__ring';
    const label = document.createElement('span');
    label.className = 'fw-cursor__label';
    ring.append(label);
    el.append(ring);
    document.body.append(el);
    const xTo = gsap.quickTo(el, 'x', { duration: 0.35, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.35, ease: 'power3.out' });
    let first = true;
    const opts = { signal, passive: true };
    window.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        if (first) {
          gsap.set(el, { x: event.clientX, y: event.clientY });
          first = false;
        }
        xTo(event.clientX);
        yTo(event.clientY);
        el.classList.add('is-on');
      },
      opts
    );
    document.addEventListener(
      'pointerover',
      (event) => {
        const t = event.target;
        if (!(t instanceof Element)) return;
        if (t.closest('input, textarea, select, iframe, dialog, .fw-hotspot__card')) {
          el.classList.remove('is-label', 'is-link');
          return;
        }
        const hit = LABELS.map(([sel, text]) => {
          const node = t.closest(sel);
          return node ? text(node) : '';
        }).find(Boolean);
        const onControl = t.closest('a, button, [role="tab"], label, summary');
        const text = hit && !t.closest('button, .fw-card__meta, .fw-hotspot') ? hit : '';
        label.textContent = text;
        el.classList.toggle('is-label', Boolean(text));
        el.classList.toggle('is-link', !text && Boolean(onControl));
      },
      opts
    );
    document.addEventListener('pointerdown', () => el.classList.add('is-down'), opts);
    document.addEventListener('pointerup', () => el.classList.remove('is-down'), opts);
    document.documentElement.addEventListener('pointerleave', () => el.classList.remove('is-on'), opts);
    return () => el.remove();
  }

  /* ---------- Header nav: letters roll up on hover ---------- */
  function navRoll({ fine }) {
    if (!fine) return undefined;
    document.querySelectorAll('#header-component .menu-list__link-title').forEach((t) => {
      if (t.querySelector('.fw-roll')) return;
      const text = t.textContent.trim();
      const roll = document.createElement('span');
      roll.className = 'fw-roll';
      roll.dataset.text = text; // the rolling copy is CSS content with empty alt text, so it is read once
      const inner = document.createElement('span');
      inner.className = 'fw-roll__in';
      inner.textContent = text;
      roll.append(inner);
      t.replaceChildren(roll);
    });
    return undefined; // harmless to keep across breakpoints
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();